import { type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { BoardView, BoardViewInfo } from '../../types';
import { BOARD_VIEW_TYPE, viewTypeToSlug } from '../../types';

const VIEW_TYPE_I18N_KEY: Record<number, string> = {
  [BOARD_VIEW_TYPE.List]: 'list',
  [BOARD_VIEW_TYPE.Kanban]: 'kanban',
  [BOARD_VIEW_TYPE.Calendar]: 'calendar',
  [BOARD_VIEW_TYPE.Gantt]: 'gantt',
  [BOARD_VIEW_TYPE.Documents]: 'documents',
  [BOARD_VIEW_TYPE.Dashboard]: 'dashboard',
  [BOARD_VIEW_TYPE.Activity]: 'activity',
  [BOARD_VIEW_TYPE.Timeline]: 'timeline',
  [BOARD_VIEW_TYPE.Whiteboard]: 'whiteboard',
};

interface ViewTabsBarProps {
  views: BoardViewInfo[];
  activeView: BoardView;
  onViewChange: (view: BoardView) => void;
  onRemoveView: (viewId: string) => void;
  isLoading?: boolean;
  addViewControl?: ReactNode;
}

export function ViewTabsBar({
  views,
  activeView,
  onViewChange,
  onRemoveView,
  isLoading = false,
  addViewControl,
}: ViewTabsBarProps) {
  const { t } = useTranslation('boards');

  if (isLoading) {
    return (
      <div className="flex items-center gap-1" data-testid="board-views-loading">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-16" />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      {views.map((view) => {
        const slug = viewTypeToSlug(view.type);
        const isActive = activeView === slug;
        const i18nKey = VIEW_TYPE_I18N_KEY[view.type] ?? 'list';
        return (
          <div key={view.id} className="group relative flex shrink-0 items-center">
            <button
              type="button"
              onClick={() => onViewChange(slug)}
              className={cn(
                'shrink-0 px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? '-mb-px border-b-2 border-primary text-foreground'
                  : 'border-b border-border text-muted-foreground hover:text-foreground',
                views.length > 1 && 'pr-7',
              )}
              data-testid={`board-views-tab-${view.type}`}
            >
              {t(`views.types.${i18nKey}`)}
            </button>

            {views.length > 1 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0.5 top-1/2 size-5 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveView(view.id as string);
                    }}
                    data-testid={`board-views-remove-${view.type}`}
                    aria-label={t('views.removeView')}
                  >
                    <X className="size-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('views.removeView')}</TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}

      {addViewControl}
    </div>
  );
}
